import React from 'react';
import {
    Route,
    Routes,
    Navigate,
} from 'react-router-dom';

import { useAppSelector } from '../state/hooks';
import { ApiStatus } from '../utils/enums';
import { hasNetworkRxRole } from '../utils/environmentUtils';
import { routeComparator } from '../utils/navigationHelpers';
import { publicRoutes, privateRoutes } from './appRouteList';

import AuthenticatedRoute from './authenticatedRoute';
import Login from '../pages/authentication/login';
import Authorize from '../pages/authentication/authorize';
import Revoke from '../pages/authentication/revoke';
import ErrorBoundary from '../components/Errors/ErrorBoundary';
import UnauthorizedError from '../components/Errors/UnauthorizedError';
import LoginError from '../components/Errors/loginError';
import NotFoundError from '../components/Errors/NotFoundError';
import Lanes from '../pages/Lanes';
import Areas from '../pages/Areas';
import AreaBalanceDetails from '../pages/AreaBalanceDetails';
import Shippers from '../pages/Shippers';
import ShipperDetailsPage from '../pages/ShipperDetailsPage';
import HealthAssessment from '../pages/HealthAssessment';
import AreaHealth from '../pages/AreaHealth';
import ShipperHealth from '../pages/ShipperHealth';
import ShipperHealthDetails from '../pages/ShipperHealthDetails';
import NetworkRx from '../pages/NetworkRx';

const AllRoutes = (): React.JSX.Element => {
    const userLoginStatus = useAppSelector((state) => { return state.user.userLoginStatus; });
    const userDetails = useAppSelector((state) => { return state.user.userDetails; });

    if (userLoginStatus === ApiStatus.Failure) {
        return (
            <Routes>
                <Route
                    path='*'
                    element={
                        <ErrorBoundary>
                            <LoginError />
                        </ErrorBoundary>
                    }
                />
            </Routes>
        );
    }

    const redirectUrl = sessionStorage.getItem('networkInsightsRedirectUrl');
    // @ts-ignore
    const isRedirectUrlPublicRoute = routeComparator(Object.values(publicRoutes), redirectUrl);
    let appRedirectUrl: string = privateRoutes.lanes;
    if (!isRedirectUrlPublicRoute) {
        // @ts-ignore
        appRedirectUrl = redirectUrl;
    }

    const hasNetworkRxPermission = hasNetworkRxRole({ userLoginStatus, userDetails });

    return (
        <Routes>
            {
                userLoginStatus === ApiStatus.Success &&
                <Route
                    path={publicRoutes.authorize}
                    element={<Navigate to={appRedirectUrl} />}
                />
            }
            <Route path={publicRoutes.root} element={<ErrorBoundary><Login /></ErrorBoundary>} />
            <Route path={publicRoutes.authorize} element={<ErrorBoundary><Authorize /></ErrorBoundary>} />
            <Route path={publicRoutes.revoke} element={<ErrorBoundary><Revoke /></ErrorBoundary>} />
            <Route path={publicRoutes.notAuthorized} element={<ErrorBoundary><UnauthorizedError /></ErrorBoundary>} />
            <Route path={publicRoutes.notFound} element={<ErrorBoundary><NotFoundError /></ErrorBoundary>} />

            {
                userLoginStatus === ApiStatus.Loading &&
                <Route
                    path='*'
                    element={<Navigate to={publicRoutes.root} />}
                />
            }

            <Route
                path={privateRoutes.lanes}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <Lanes />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.areas}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <Areas />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.areaBalance}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <AreaBalanceDetails />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.shippers}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <Shippers />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.shipperDetails}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <ShipperDetailsPage />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.healthAssessment}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <HealthAssessment />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.areaHealth}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <AreaHealth />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.shipperHealth}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <ShipperHealth />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            <Route
                path={privateRoutes.shipperHealthDetails}
                element={
                    <React.StrictMode>
                        <AuthenticatedRoute>
                            <ErrorBoundary>
                                <ShipperHealthDetails />
                            </ErrorBoundary>
                        </AuthenticatedRoute>
                    </React.StrictMode>
                }
            />

            {
                hasNetworkRxPermission &&
                <Route
                    path={privateRoutes.networkRx}
                    element={
                        <React.StrictMode>
                            <AuthenticatedRoute>
                                <ErrorBoundary>
                                    <NetworkRx />
                                </ErrorBoundary>
                            </AuthenticatedRoute>
                        </React.StrictMode>
                    }
                />
            }

            <Route path='*' element={<ErrorBoundary><NotFoundError /></ErrorBoundary>} />
        </Routes>
    );
};

export default AllRoutes;
